import { adminPanelHref } from "@/lib/admin-panel-url";
import {
  useReactivateSuperAdminShop,
  useRefreshSuperAdminShopStats,
  useSuperAdminShopDetail,
  useSuperAdminShops,
  useUpdateSuperAdminShopMeta,
} from "@/api/super-admin/use-super-admin-shops";
import { useSyncCancelledReturnRequestsStats } from "@/api/super-admin/use-super-admin-statistics";
import {
  ArrowLeftRight,
  Clock,
  ExternalLink,
  FlaskConical,
  Gift,
  Globe,
  Package,
  RefreshCw,
  RotateCcw,
  Save,
  TrendingUp,
  Truck,
} from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AdminPageShell } from "./admin-tab-pages";
import {
  CLIENT_STATUS_LABELS,
  countryFlag,
  FeatureBadge,
  formatDate,
  formatRelative,
  INTEGRATIONS,
  PercentBar,
  type SelectedShop,
  shopToSelected,
  StatPanel,
} from "./shop-shared";

// ─── Toggle ───────────────────────────────────────────────────────────────────

function Toggle({ on, color }: { on: boolean; color: "amber" | "green" }) {
  const trackOn = color === "amber" ? "bg-amber-500" : "bg-green-500";
  return (
    <div
      className={[
        "relative h-5 w-9 rounded-full transition-colors shrink-0 pointer-events-none",
        on ? trackOn : "bg-muted-foreground/30",
      ].join(" ")}
    >
      <span
        className={[
          "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform",
          on ? "translate-x-4" : "translate-x-0.5",
        ].join(" ")}
      />
    </div>
  );
}

// ─── Shop Meta Editor ─────────────────────────────────────────────────────────

function ShopMetaEditor({
  shop,
  onSaved,
}: {
  shop: SelectedShop;
  onSaved: (updated: Partial<SelectedShop>) => void;
}) {
  const updateMutation = useUpdateSuperAdminShopMeta();

  const [isTestShop, setIsTestShop] = useState(shop.isTestShop ?? false);
  const [isIncludedInStats, setIsIncludedInStats] = useState(
    shop.isIncludedInStats ?? true,
  );
  const [saved, setSaved] = useState(false);

  async function handleSave() {
    const result = await updateMutation.mutateAsync({
      params: { path: { shopId: shop.id } },
      body: {
        isTestShop: isTestShop || null,
        // null = "include by default"; false = explicitly excluded
        isIncludedInStats: isTestShop
          ? false
          : isIncludedInStats
            ? null
            : false,
      },
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    onSaved({
      isTestShop: result?.isTestShop ?? null,
      isIncludedInStats: result?.isIncludedInStats ?? null,
    });
  }

  return (
    <section className="border-t px-6 py-5 space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        Infos internes
      </h3>

      {/* The client relationship is edited on the client (BBS-406). */}
      <p className="text-sm">
        <span className="text-muted-foreground">Client : </span>
        {shop.clientAccountId ? (
          <a
            href={adminPanelHref(`/shared/clients/${shop.clientAccountId}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-violet-700 hover:underline"
          >
            {shop.clientName}
          </a>
        ) : (
          <span className="text-muted-foreground">aucun</span>
        )}
        <Link
          to={`/admin/shops/${shop.id}/configuration`}
          className="ml-3 text-xs text-violet-700 hover:underline"
        >
          Configuration
        </Link>
      </p>

      <div className="grid grid-cols-2 gap-4">
        {/* isTestShop */}
        <div
          role="switch"
          aria-checked={isTestShop}
          tabIndex={0}
          onClick={() => setIsTestShop((v) => !v)}
          onKeyDown={(e) =>
            e.key === " " || e.key === "Enter" ? setIsTestShop((v) => !v) : null
          }
          className="flex items-center justify-between gap-2 rounded-md border p-3 cursor-pointer hover:bg-muted/30 transition-colors select-none"
        >
          <div>
            <div className="text-sm font-medium">Shop de test</div>
            <div className="text-xs text-muted-foreground">
              Exclut des stats par défaut
            </div>
          </div>
          <Toggle on={isTestShop} color="amber" />
        </div>

        {/* isIncludedInStats */}
        <div
          role="switch"
          aria-checked={!isTestShop && isIncludedInStats}
          tabIndex={isTestShop ? -1 : 0}
          onClick={() => !isTestShop && setIsIncludedInStats((v) => !v)}
          onKeyDown={(e) =>
            !isTestShop && (e.key === " " || e.key === "Enter")
              ? setIsIncludedInStats((v) => !v)
              : null
          }
          className={[
            "flex items-center justify-between gap-2 rounded-md border p-3 transition-colors select-none",
            isTestShop
              ? "opacity-40 cursor-not-allowed"
              : "cursor-pointer hover:bg-muted/30",
          ].join(" ")}
        >
          <div>
            <div className="text-sm font-medium">Inclus dans les stats</div>
            <div className="text-xs text-muted-foreground">
              Compte dans les agrégats
            </div>
          </div>
          <Toggle on={!isTestShop && isIncludedInStats} color="green" />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={updateMutation.isPending}
          className={[
            "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50",
            saved
              ? "bg-green-600 text-white"
              : "bg-violet-600 text-white hover:bg-violet-700",
          ].join(" ")}
        >
          <Save className="h-3 w-3" />
          {updateMutation.isPending
            ? "Enregistrement…"
            : saved
              ? "Enregistré ✓"
              : "Enregistrer"}
        </button>
      </div>
    </section>
  );
}

// ─── Shop Detail Page ─────────────────────────────────────────────────────────

/**
 * A shop seen from the super admin: stats, features and internal metadata.
 * The row comes from the fleet overview (cached), the breakdowns from the
 * shop's own detail route.
 */
export function AdminShopDetailPage() {
  const { shopId } = useParams<{ shopId: string }>();
  const {
    data: overview,
    isPending: isOverviewPending,
    refetch: refetchOverview,
  } = useSuperAdminShops();
  const { data, isPending, isError, refetch } = useSuperAdminShopDetail(
    shopId ?? null,
  );
  const refreshMutation = useRefreshSuperAdminShopStats();
  const reactivateMutation = useReactivateSuperAdminShop();
  const syncCancelledMutation = useSyncCancelledReturnRequestsStats();

  const shops = overview?.shops;
  const row = shops?.find((s) => s.id === shopId);
  const shop = row
    ? shopToSelected(row as Parameters<typeof shopToSelected>[0])
    : null;

  if (isOverviewPending) {
    return (
      <AdminPageShell title="Boutique">
        <p className="text-sm text-muted-foreground">Chargement…</p>
      </AdminPageShell>
    );
  }

  if (!shop) {
    return (
      <AdminPageShell title="Boutique">
        <p className="text-sm text-muted-foreground">
          Boutique introuvable. <Link to="/admin/shops">Retour à la liste</Link>
        </p>
      </AdminPageShell>
    );
  }

  async function handleReactivate() {
    if (!shop) return;
    await reactivateMutation.mutateAsync({
      params: { path: { shopId: shop.id } },
    });
    await refetchOverview();
  }

  async function handleShopRefresh() {
    if (!shop) return;
    await refreshMutation.mutateAsync({
      params: { path: { shopId: shop.id } },
    });
    await Promise.all([refetch(), refetchOverview()]);
  }

  return (
    <AdminPageShell
      title={shop.name}
      actions={
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleShopRefresh}
            disabled={refreshMutation.isPending}
            className="flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
            title="Recalculer les stats de ce shop"
          >
            <RefreshCw
              className={`h-3 w-3 ${refreshMutation.isPending ? "animate-spin" : ""}`}
            />
            {refreshMutation.isPending ? "Calcul…" : "Rafraîchir"}
          </button>
          <button
            onClick={() =>
              syncCancelledMutation.mutate({
                params: { query: { shopId: shop.id } },
              })
            }
            disabled={syncCancelledMutation.isPending}
            className="flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
            title="Backfill isCancelled dans les stats pour ce shop (idempotent)"
          >
            <RotateCcw
              className={`h-3 w-3 ${syncCancelledMutation.isPending ? "animate-spin" : ""}`}
            />
            {syncCancelledMutation.isPending
              ? "Sync…"
              : syncCancelledMutation.isSuccess
                ? `✓ ${(syncCancelledMutation.data as { updated: number } | undefined)?.updated ?? 0} màj`
                : "Sync annulations"}
          </button>
          {shop.isActive === false && (
            <button
              onClick={handleReactivate}
              disabled={reactivateMutation.isPending}
              className="flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50"
              title="Réactiver ce shop (isActive=true, lastActivityAt=now)"
            >
              <TrendingUp className="h-3 w-3" />
              {reactivateMutation.isPending ? "Réactivation…" : "Réactiver"}
            </button>
          )}
          {/* An exit to the Returns app: new tab, the admin stays open. */}
          <a
            href={`/shops/${shop.id}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            Ouvrir dans Returns
          </a>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 -mt-2">
        <span className="text-xs text-muted-foreground font-mono">
          {shop.slug}
        </span>
        {shop.isTestShop && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
            <FlaskConical className="h-3 w-3" />
            Test
          </span>
        )}
        {shop.isActive === false && (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
            Inactif
          </span>
        )}
        {shop.clientStatus && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${CLIENT_STATUS_LABELS[shop.clientStatus]?.color ?? ""}`}
          >
            {CLIENT_STATUS_LABELS[shop.clientStatus]?.label ??
              shop.clientStatus}
          </span>
        )}
        {shop.lastActivityAt && (
          <span className="text-xs text-muted-foreground">
            Activité : {formatDate(shop.lastActivityAt)}
          </span>
        )}
        {data?.statsComputedAt && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            {formatRelative(data.statsComputedAt)}
          </span>
        )}
      </div>

      <div className="rounded-lg border bg-background">
        {isPending && (
          <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
            Chargement…
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-20 text-destructive text-sm">
            Erreur lors du chargement
          </div>
        )}

        {data && (
          <div className="p-6 space-y-6">
            {/* Period stat cards */}
            <div className="grid grid-cols-4 gap-3">
              <StatPanel
                period="Total"
                orders={data.totalOrders}
                returns={data.totalReturnRequests}
              />
              <StatPanel
                period="30 jours"
                orders={shop.ordersLast30Days}
                returns={shop.returnRequestsLast30Days}
                color="amber"
              />
              <StatPanel
                period="7 jours"
                orders={shop.ordersLast7Days}
                returns={shop.returnRequestsLast7Days}
                color="emerald"
              />
              <StatPanel
                period="24h"
                orders={shop.ordersLast24Hours}
                returns={shop.returnRequestsLast24Hours}
                color="blue"
              />
            </div>

            {/* Feature flags + integrations */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <FeatureBadge
                  icon={<ArrowLeftRight className="h-3 w-3" />}
                  label="Échanges"
                  active={shop.hasExchanges}
                  activeColor="bg-blue-100 text-blue-700 border-blue-200"
                />
                <FeatureBadge
                  icon={<Gift className="h-3 w-3" />}
                  label="Avoirs"
                  active={shop.hasGiftCode}
                  activeColor="bg-violet-100 text-violet-700 border-violet-200"
                />
                <FeatureBadge
                  icon={<Package className="h-3 w-3" />}
                  label="Workflow logisticien"
                  active={shop.hasWorkflowLogistician}
                  activeColor="bg-orange-100 text-orange-700 border-orange-200"
                />
              </div>
              {shop.activeIntegrations.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {shop.activeIntegrations.map((key) => {
                    const info = INTEGRATIONS[key];
                    return (
                      <span
                        key={key}
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${info?.color ?? "bg-muted text-muted-foreground border-transparent"}`}
                      >
                        {info?.label ?? key}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Two columns: countries + carriers */}
            <div className="grid grid-cols-2 gap-6">
              {/* By Country */}
              <section>
                <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  Retours par pays
                  <span className="text-xs text-muted-foreground font-normal">
                    {data.breakdownWindowDays} derniers jours
                  </span>
                  {data.byCountry.length > 0 && (
                    <span className="text-xs text-muted-foreground font-normal">
                      top {data.byCountry.length}
                    </span>
                  )}
                </h3>
                {data.byCountry.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Aucune donnée</p>
                ) : (
                  <div className="space-y-2">
                    {data.byCountry.map((row) => (
                      <div
                        key={row.countryCode}
                        className="flex items-center gap-2"
                      >
                        <span className="text-sm leading-none w-5 text-center shrink-0">
                          {countryFlag(row.countryCode)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-medium truncate">
                              {row.country}
                            </span>
                            <span className="text-xs tabular-nums text-muted-foreground ml-2 shrink-0">
                              {row.returnCount.toLocaleString("fr-FR")}
                            </span>
                          </div>
                          <PercentBar pct={row.percentage} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* By Carrier */}
              <section>
                <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
                  <Truck className="h-4 w-4 text-muted-foreground" />
                  Transporteurs
                  <span className="text-xs text-muted-foreground font-normal">
                    {data.breakdownWindowDays} derniers jours
                  </span>
                  {data.byCarrier.length > 0 && (
                    <span className="text-xs text-muted-foreground font-normal">
                      top {data.byCarrier.length}
                    </span>
                  )}
                </h3>
                {data.byCarrier.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Aucune donnée</p>
                ) : (
                  <div className="space-y-2">
                    {data.byCarrier.map((row) => (
                      <div
                        key={row.carrier}
                        className="flex items-center gap-2"
                      >
                        <span className="text-xs font-mono font-medium w-36 shrink-0 truncate">
                          {row.carrier}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="sr-only">{row.carrier}</span>
                            <span className="text-xs tabular-nums text-muted-foreground ml-auto shrink-0">
                              {row.count.toLocaleString("fr-FR")}
                            </span>
                          </div>
                          <PercentBar pct={row.percentage} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </div>
        )}

        <ShopMetaEditor
          key={shop.id}
          shop={shop}
          onSaved={() => void refetchOverview()}
        />
      </div>
    </AdminPageShell>
  );
}
